/**
 * Peças de tela usadas em todo o app.
 */
import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react'

export type Aba = 'origem' | 'negociacao' | 'pesagem' | 'romaneio' | 'pagamento' | 'custo'
export type Rota =
  | { tela: 'fichas' }
  | { tela: 'nova' }
  | { tela: 'ficha'; id: string; aba: Aba }
  | { tela: 'produtores' }
  | { tela: 'ajustes' }
  | { tela: 'equipe' }
  | { tela: 'plataforma' }
  | { tela: 'extrato' }
  /** D3 · catira; `fichaId` = veio do «Ver na catira» de uma ficha */
  | { tela: 'catira'; fichaId?: string }
  /** D4 · notas fiscais (registrar, nunca emitir) */
  | { tela: 'notas' }
  /** R2B · contas a pagar e a receber (gestor e Consulta) */
  | { tela: 'contas' }
  /** P3 · relatório de receitas e despesas (gestor e Consulta) */
  | { tela: 'relatorio' }
export type Ir = (r: Rota) => void

/* ---------- aviso rápido no rodapé ---------- */
let mostrarAviso: (t: string) => void = () => {}
export function avisar(t: string): void { mostrarAviso(t) }

export function Avisos() {
  const [texto, setTexto] = useState('')
  const [visivel, setVisivel] = useState(false)
  useEffect(() => {
    let t: ReturnType<typeof setTimeout>
    mostrarAviso = (s) => {
      setTexto(s); setVisivel(true)
      clearTimeout(t); t = setTimeout(() => setVisivel(false), 2400)
    }
  }, [])
  return <div className={`aviso-rapido nao-imprimir${visivel ? ' on' : ''}`}>{texto}</div>
}

/* ---------- campos ---------- */
export function Campo(p: {
  rotulo: string; valor: string; mudar: (v: string) => void
  tipo?: string; modo?: 'decimal' | 'numeric' | 'tel' | 'text'; dica?: string
  lista?: string; maiusculas?: boolean; max?: number; desabilitado?: boolean; id?: string
}) {
  return (
    <label className="campo">
      <span>{p.rotulo}</span>
      <input
        id={p.id}
        type={p.tipo ?? 'text'}
        inputMode={p.modo}
        value={p.valor}
        placeholder={p.dica}
        list={p.lista}
        maxLength={p.max}
        disabled={p.desabilitado}
        autoComplete="off"
        style={p.maiusculas ? { textTransform: 'uppercase' } : undefined}
        onChange={(e) => p.mudar(e.target.value)}
      />
    </label>
  )
}

export function Escolha<T extends string>(p: {
  rotulo: string; valor: T; mudar: (v: T) => void; opcoes: [T, string][]; desabilitado?: boolean
}) {
  return (
    <label className="campo">
      <span>{p.rotulo}</span>
      <select value={p.valor} disabled={p.desabilitado} onChange={(e) => p.mudar(e.target.value as T)}>
        {p.opcoes.map(([v, t]) => <option key={v} value={v}>{t}</option>)}
      </select>
    </label>
  )
}

/** Botões lado a lado, um aceso — para escolhas de dois ou três caminhos. */
export function Segmento<T extends string>(p: { valor: T; mudar: (v: T) => void; opcoes: [T, string][]; desabilitado?: boolean }) {
  return (
    <div className="segmento">
      {p.opcoes.map(([v, t]) => (
        <button key={v} type="button" disabled={p.desabilitado} className={v === p.valor ? 'on' : ''} onClick={() => p.mudar(v)}>{t}</button>
      ))}
    </div>
  )
}

/** Quantidade: número com − e + ao lado. */
export function Contador(p: { valor: number; mudar: (n: number) => void; minimo?: number }) {
  const min = p.minimo ?? 1
  const [texto, setTexto] = useState(String(p.valor))
  useEffect(() => { setTexto(String(p.valor)) }, [p.valor])
  return (
    <div className="contador">
      <button type="button" onClick={() => p.mudar(Math.max(min, p.valor - 1))} aria-label="menos um">−</button>
      <input
        inputMode="numeric"
        value={texto}
        onChange={(e) => {
          setTexto(e.target.value)
          const n = parseInt(e.target.value, 10)
          if (n >= min) p.mudar(n)
        }}
        onBlur={() => setTexto(String(p.valor))}
      />
      <button type="button" onClick={() => p.mudar(p.valor + 1)} aria-label="mais um">+</button>
    </div>
  )
}

export function Dica(p: { children: ReactNode }) {
  return <div className="dica">{p.children}</div>
}

export function Tarja(p: { children: ReactNode; tipo?: 'alerta' | 'erro' | 'ok'; onClick?: () => void }) {
  return <div className={`tarja ${p.tipo ?? 'alerta'}${p.onClick ? ' clicavel' : ''}`} onClick={p.onClick}>{p.children}</div>
}

/* ---------- janela de diálogo ---------- */
export function Janela(p: { titulo: string; children: ReactNode; fechar: () => void }) {
  return (
    <div className="janela-fundo nao-imprimir" onClick={(e) => { if (e.target === e.currentTarget) p.fechar() }}>
      <div className="janela">
        <div className="janela-titulo">{p.titulo}</div>
        {p.children}
      </div>
    </div>
  )
}

/** Pergunta o motivo de reabrir/cancelar. Devolve null se a pessoa desistiu. */
export function pedirMotivo(pergunta: string, obrigatorio: boolean): string | null {
  const r = window.prompt(pergunta, '')
  if (r === null) return null
  if (obrigatorio && !r.trim()) { window.alert('O motivo é obrigatório.'); return null }
  return r.trim()
}

/* ---------- R1C · confirmações e motivos na janela do próprio app ---------- */

/**
 * Revisão de 06/10/2026: o `window.confirm` e o `window.prompt` do navegador
 * saem pequenos, com o endereço do site no topo, e no celular parecem aviso
 * de outro lugar. As confirmações de risco (excluir, reabrir, cancelar,
 * encerrar) agora abrem uma janela do app, com botões grandes. Promessa: quem
 * chama espera a resposta (`await confirmar(...)`).
 */
interface PedidoDeConfirmacao {
  tipo: 'confirmar'; titulo: string; texto?: ReactNode; sim: string; nao: string; perigo?: boolean
  responder: (v: boolean) => void
}
interface PedidoDeMotivo {
  tipo: 'motivo'; titulo: string; texto?: ReactNode; sim: string; atalhos: string[]; perigo?: boolean
  responder: (v: string | null) => void
}
type Pedido = PedidoDeConfirmacao | PedidoDeMotivo
let abrirPedido: (p: Pedido) => void = () => {}

export function confirmar(o: { titulo: string; texto?: ReactNode; sim?: string; nao?: string; perigo?: boolean }): Promise<boolean> {
  return new Promise((responder) => abrirPedido({ tipo: 'confirmar', sim: 'Confirmar', nao: 'Voltar', ...o, responder }))
}

/** Os casos reais que o gestor contou, para não digitar no curral. */
export const MOTIVOS_DE_REABERTURA = ['boi garimpado depois da lida', 'boi fugiu no embarque', 'peso digitado errado']

/** Motivo OBRIGATÓRIO, com atalhos de um toque. Devolve null se a pessoa desistiu. */
export function pedirMotivoNaJanela(o: { titulo: string; texto?: ReactNode; sim: string; atalhos?: string[]; perigo?: boolean }): Promise<string | null> {
  return new Promise((responder) => abrirPedido({ tipo: 'motivo', atalhos: MOTIVOS_DE_REABERTURA, ...o, responder }))
}

/** Mora uma vez no App, como os Avisos: mostra o pedido da vez. */
export function Dialogos() {
  const [p, setP] = useState<Pedido | null>(null)
  const [motivo, setMotivo] = useState('')
  const [erro, setErro] = useState('')
  useEffect(() => {
    abrirPedido = (novo) => { setMotivo(''); setErro(''); setP(novo) }
    return () => { abrirPedido = () => {} }
  }, [])
  if (!p) return null
  const fechar = () => { if (p.tipo === 'confirmar') p.responder(false); else p.responder(null); setP(null) }
  const aceitar = () => {
    if (p.tipo === 'confirmar') { p.responder(true); setP(null); return }
    if (!motivo.trim()) { setErro('O motivo é obrigatório.'); return }
    p.responder(motivo.trim()); setP(null)
  }
  return (
    <Janela titulo={p.titulo} fechar={fechar}>
      {p.texto && <div className="janela-texto">{p.texto}</div>}
      {p.tipo === 'motivo' && (
        <>
          <label className="campo"><span>Motivo (obrigatório)</span>
            <input autoFocus value={motivo} onChange={(e) => { setMotivo(e.target.value); setErro('') }}
              onKeyDown={(e) => { if (e.key === 'Enter') aceitar() }} /></label>
          <div className="atalhos-motivo">
            {p.atalhos.map((a) => <button key={a} type="button" className="mini" onClick={() => { setMotivo(a); setErro('') }}>{a}</button>)}
          </div>
          {erro && <div className="alerta">{erro}</div>}
        </>
      )}
      <button type="button" className={`botao${p.perigo ? ' perigo-cheio' : ''}`} onClick={aceitar}>{p.sim}</button>
      <button type="button" className="botao fantasma" onClick={fechar}>{p.tipo === 'confirmar' ? p.nao : 'Voltar'}</button>
    </Janela>
  )
}

/* ---------- R1C · formulário preenchido que sumiria ao trocar de tela ---------- */

/**
 * Revisão de 06/10/2026: a «Nova pesagem» preenchida sumia sem aviso ao tocar
 * em Fichas. A tela que tem rascunho não gravado se registra aqui; o App
 * pergunta antes de trocar de tela.
 */
let guarda: (() => string | null) | null = null
export function usarGuardaDeSaida(pergunta: () => string | null): void {
  const atual = useRef(pergunta)
  atual.current = pergunta
  useEffect(() => {
    const g = () => atual.current()
    guarda = g
    return () => { if (guarda === g) guarda = null }
  }, [])
}

/** true: pode sair (não há rascunho, ou a pessoa aceitou descartar). */
export async function podeSairDaTela(): Promise<boolean> {
  const texto = guarda?.()
  if (!texto) return true
  const ok = await confirmar({ titulo: 'Descartar a nova pesagem?', texto, sim: 'Descartar', nao: 'Continuar preenchendo', perigo: true })
  if (ok) guarda = null
  return ok
}

/* ---------- 3D · gravação automática ---------- */

/**
 * Grava sozinho o rascunho de uma tela de ficha aberta (Origem, Negociação,
 * GTA do romaneio). Pedido do gestor no test drive de 06/10/2026: indo e
 * voltando entre as abas, o «Salvar e seguir» ficava sempre à vista e deixava
 * a dúvida «será que está salvo?». Agora a tela grava a cada mudança, com uma
 * pausa curta (não a cada tecla), e grava o que faltar ao sair dela.
 *
 *  - `chave`: o que seria gravado, em texto. Mudou → grava depois da pausa.
 *    Quem chama monta a chave só com o que é VÁLIDO (lote sem categoria fica
 *    fora), para não gravar rascunho pela metade.
 *  - `gravar`: escreve na ficha. Deve conferir se algo mudou de fato antes de
 *    chamar `mudar()`: cada gravação marca a ficha como pendente (uma marca
 *    só, por ficha) e põe a hora da alteração — sem versão nova nem registro novo.
 *  - `aoSair`: o que só se faz uma vez, ao sair da tela, e só se algo foi
 *    gravado: pôr o produtor ou o cliente no cadastro (gravar o nome a cada
 *    pausa deixaria «Jo», «João P»… no cadastro) e anotar no histórico.
 *
 * Devolve a situação («salvando…» / «✓ salvo») e `sair()`, que o botão
 * «Próximo» chama antes de trocar de aba.
 */
export function usarGravacaoAutomatica(o: { ativo: boolean; chave: string; gravar: () => void; aoSair?: () => void; espera?: number }) {
  const [situacao, setSituacao] = useState<'salvando' | 'salvo'>('salvo')
  const ultima = useRef(o.chave)
  const relogio = useRef<ReturnType<typeof setTimeout> | null>(null)
  const atual = useRef(o)
  atual.current = o
  // algo foi gravado nesta visita à tela: `aoSair` tem o que fazer
  const gravou = useRef(false)

  const jaGravar = useCallback(() => {
    if (relogio.current) { clearTimeout(relogio.current); relogio.current = null }
    const x = atual.current
    if (x.ativo && x.chave !== ultima.current) {
      x.gravar()
      ultima.current = x.chave
      gravou.current = true
    }
    setSituacao('salvo')
  }, [])

  const sair = useCallback(() => {
    jaGravar()
    if (gravou.current && atual.current.ativo) atual.current.aoSair?.()
    gravou.current = false
  }, [jaGravar])

  useEffect(() => {
    if (!o.ativo || o.chave === ultima.current) return
    setSituacao('salvando')
    if (relogio.current) clearTimeout(relogio.current)
    relogio.current = setTimeout(jaGravar, o.espera ?? 600)
  }, [o.ativo, o.chave, o.espera, jaGravar])

  // ao sair da tela (outra aba, outra ficha) e ao fechar o app: grava o que faltou
  useEffect(() => {
    window.addEventListener('pagehide', sair)
    return () => { window.removeEventListener('pagehide', sair); sair() }
  }, [sair])

  return { situacao, sair }
}

/** O sinal discreto da gravação automática. */
export function SinalDeSalvo({ situacao }: { situacao: 'salvando' | 'salvo' }) {
  return <span className={`sinal-salvo${situacao === 'salvo' ? ' ok' : ''}`} aria-live="polite">{situacao === 'salvo' ? '✓ salvo' : 'salvando…'}</span>
}

/**
 * 3D · A linha embaixo de um nome que vai para o cadastro (produtor, cliente,
 * local próprio): «✓ do cadastro», «novo — entra no cadastro» e, se há um
 * nome parecido, o aviso com um toque para usar o do cadastro.
 */
export function AvisoDeNome({ nome, doCadastro, parecido, usar, novo }: {
  nome: string; doCadastro: boolean; parecido?: string; usar: (nome: string) => void; novo: string
}) {
  if (!nome.trim()) return <div className="mini-dica" />
  if (doCadastro) return <div className="mini-dica">✓ do cadastro</div>
  // R1C: com nome parecido, só o aviso de parecido — duas frases juntas confundiam
  if (parecido) {
    return (
      <div className="mini-dica">
        <b>Parecido com «{parecido}»</b> — é o mesmo?{' '}
        <button type="button" className="mini" onClick={() => usar(parecido)}>usar «{parecido}»</button>
      </div>
    )
  }
  return <div className="mini-dica">{novo}</div>
}
